<template>
	<div>
		<ModalHeader :title="getString('permissions')" :button-text="getString('confirm')" @click-button="$emit('confirm')"/>

		<AppList :items="permissionsMap">
			<template #granular-permissions>
				<AppList :items="orderPermissions" >
					<template #order-pay-granular-permissions>
						<AppList :items="orderPayPermissions" />
					</template>
				</AppList>
			</template>
		</AppList>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";

import {useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores"

export default {
	setup() {
		let userStore = useUserStore();
		return {getString: useLanguageStore().getString, userStore};
	},
	emits: ['confirm'],
	name: "PermissionsEditorBottomSheet",
	components: {ModalHeader, AppList},
	props: {
		active: {
			type: Boolean,
			default: false
		},
		userId: {
			type: String,
			default: 0
		}
	},
	computed: {
		currPermissions() {
			if(this.userStore.businessUsers) {
				return this.userStore.businessUsers[this.userId] ? this.userStore.businessUsers[this.userId].permission : [];
			}
			else {
				return [];
			}
		},
		permissionsMap() {
			let diffOrderPermissions = this.currPermissions.filter(permission => this.orderPermissionsKeys.includes(permission));
			return [
				{
					id: 'orders',
					title: this.getString('orders'),
					explain: this.getString('orders_permissions_explain'),
					switch: true,
					active: diffOrderPermissions.length > 0,
					dropdownSlot: 'granular-permissions',
					callback: (active) => {
						if(active) {
							this.addPermissions(this.orderPermissionsKeys);
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => !this.orderPermissionsKeys.includes(permission));
						}
					}
				},
				{
					id: 'analytics',
					title: this.getString('analytics'),
					explain: this.getString('analytics_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('analytics'),
					callback: (active) => {
						if(active) {
							this.addPermissions('analytics');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'analytics');
						}
					}
				},
				{
					id: 'marketing',
					title: this.getString('permissions_marketing'),
					explain: this.getString('permissions_marketing_description'),
					switch: true,
					active: this.currPermissions.includes('marketing'),
					callback: (active) => {
						if(active) {
							this.addPermissions('marketing');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'marketing');
						}
					}
				},
				{
					id: 'menu',
					title: this.getString('menu'),
					explain: this.getString('menu_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('menu'),
					callback: (active) => {
						if(active) {
							this.addPermissions('menu');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'menu');
						}
					}
				},
				{
					id: 'admin',
					title: this.getString('admin'),
					explain: this.getString('admin_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('admin'),
					callback: (active) => {
						if(active) {
							this.addPermissions('admin');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'admin');
						}
					}
				}
			]
		},
		orderPermissions() {
			let diffOrderPayPermissions = this.currPermissions.filter(permission => this.orderPayPermissionsKeys.includes(permission));
			return [
				{
					id: 'orders_creation',
					title: this.getString('orders_creation'),
					explain: this.getString('orders_creation_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_creation'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_creation');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_creation');
						}
					}
				},
				{
					id: 'orders_edit',
					title: this.getString('orders_edit'),
					explain: this.getString('orders_edit_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_edit'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_edit');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_edit');
						}
					}
				},
				{
					id: 'orders_pay',
					title: this.getString('orders_pay'),
					explain: this.getString('orders_pay_permissions_explain'),
					switch: true,
					dropdownSlot: 'order-pay-granular-permissions',
					active: diffOrderPayPermissions.length > 0,
					callback: (active) => {
						if(active) {
							this.addPermissions(this.orderPayPermissionsKeys);
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => !this.orderPayPermissionsKeys.includes(permission));
						}
					}
				},
				{
					id: 'orders_discounts',
					title: this.getString('orders_discounts'),
					explain: this.getString('orders_discounts_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_discounts'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_discounts');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_discounts');
						}
					}
				},
				{
					id: 'orders_daily_closing',
					title: this.getString('orders_daily_closing'),
					explain: this.getString('orders_daily_closing_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_daily_closing'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_daily_closing');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_daily_closing');
						}
					}
				}
			]
		},
		orderPayPermissions() {
			return [
				{
					id: 'orders_pay_pos',
					title: this.getString('pos'),
					explain: this.getString('orders_pay_pos_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_pay_pos'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_pay_pos');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_pay_pos');
						}
					}
				},
				{
					id: 'orders_pay_cash',
					title: this.getString('payment_type_cash'),
					explain: this.getString('orders_pay_cash_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_pay_cash'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_pay_cash');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_pay_cash');
						}
					}
				},
				{
					id: 'orders_pay_other',
					title: this.getString('orders_pay_other'),
					explain: this.getString('orders_pay_other_permissions_explain'),
					switch: true,
					active: this.currPermissions.includes('orders_pay_other'),
					callback: (active) => {
						if(active) {
							this.addPermissions('orders_pay_other');
						}
						else {
							this.userStore.businessUsers[this.userId].permission = this.userStore.businessUsers[this.userId].permission
								.filter(permission => permission !== 'orders_pay_other');
						}
					}
				}
			];
		},
		orderPermissionsKeys() {
			let orderPermissionsKeys = [];
			this.orderPermissions.forEach(permission => {
				if (permission.id !== 'orders_pay') {
					orderPermissionsKeys.push(permission.id);
				}
			});
			return [
				...orderPermissionsKeys,
				...this.orderPayPermissionsKeys
			];
		},
		orderPayPermissionsKeys() {
			return this.orderPayPermissions.map(permission => permission.id);
		},
	},
	methods: {
		addPermissions(permission) {
			if(this.userStore.businessUsers[this.userId]) {

				if(Array.isArray(permission)) {
					permission.forEach(perm => {
						if(!this.userStore.businessUsers[this.userId].permission.includes(perm)) {
							this.userStore.businessUsers[this.userId].permission.push(perm);
						}
					});
				}
				else {
					this.userStore.businessUsers[this.userId].permission.push(permission);
				}

			}
			else {
				let permissionValue = Array.isArray(permission) ? permission : [permission];
				this.userStore.businessUsers[this.userId] = {permission: permissionValue};
			}
		},
	}
}
</script>

<style scoped lang="scss">

</style>